import { useState, useEffect, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { updateSupportedNetworks } from '../orgs/api';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';

type NetworkDef = {
  key: string;
  label: string;
  description: string;
  alwaysOn?: boolean;
};

const NETWORKS: NetworkDef[] = [
  { key: 'ethereum', label: 'EVM', description: 'Ethereum, Polygon, Arbitrum, Base, BSC and other EVM-compatible chains', alwaysOn: true },
  { key: 'solana',   label: 'Solana',   description: 'High-throughput chain with sub-second finality' },
  { key: 'bitcoin',  label: 'Bitcoin',  description: 'UTXO-based Bitcoin network' },
  { key: 'cosmos',   label: 'Cosmos',   description: 'Interchain ecosystem using IBC protocol' },
  { key: 'near',     label: 'Near',     description: 'Sharded proof-of-stake layer 1' },
  { key: 'aptos',    label: 'Aptos',    description: 'Move-based layer 1 blockchain' },
  { key: 'stellar',  label: 'Stellar',  description: 'Payment-focused network with fast settlement' },
  { key: 'tron',     label: 'Tron',     description: 'Delegated proof-of-stake network' },
];

export function NetworksPage() {
  const { org } = useCurrentOrg();
  const qc = useQueryClient();
  const orgId = org?.id ?? '';

  const currentNetworks = org?.supported_networks ?? ['ethereum'];
  const [enabled, setEnabled] = useState<Set<string>>(new Set(currentNetworks));

  // Sync local state when org data loads or changes.
  useEffect(() => {
    if (org?.supported_networks) {
      setEnabled(new Set(org.supported_networks));
    }
  }, [org?.supported_networks]);

  const save = useMutation({
    mutationFn: () => {
      const networks = Array.from(enabled);
      if (!networks.includes('ethereum')) networks.unshift('ethereum');
      return updateSupportedNetworks(orgId, networks);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orgs'] }),
  });

  function toggle(key: string) {
    setEnabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  if (!org) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Networks</h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Choose which blockchain networks your end users can create wallets on.
          EVM is always enabled.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader
            title="Supported networks"
            description="Toggle networks to enable or disable wallet creation for each ecosystem."
          />
          <CardBody>
            <ul className="divide-y divide-line-soft">
              {NETWORKS.map((n) => (
                <li key={n.key} className="flex items-center justify-between gap-4 py-3.5 first:pt-0 last:pb-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-primary">{n.label}</p>
                    <p className="mt-0.5 text-xs text-ink-tertiary">{n.description}</p>
                  </div>
                  <NetworkToggle
                    checked={n.alwaysOn || enabled.has(n.key)}
                    disabled={n.alwaysOn}
                    onChange={() => toggle(n.key)}
                  />
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>

        <div className="flex items-center justify-end gap-4">
          {save.isError && (
            <p className="text-sm text-bad">
              {save.error instanceof Error ? save.error.message : 'Could not save'}
            </p>
          )}
          {save.isSuccess && <p className="text-sm text-ok">Saved.</p>}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving...' : 'Save changes'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function NetworkToggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'bg-bit2me' : 'bg-ink-muted'
      }`}
    >
      <span
        className={`pointer-events-none block h-5 w-5 rounded-full bg-white shadow-sm ring-0 transition-transform ${
          checked ? 'translate-x-[22px]' : 'translate-x-[2px]'
        }`}
      />
    </button>
  );
}
